import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/site";

const POLL_MAX = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (attempts >= POLL_MAX) { setState("timeout"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-24">
      <div className="text-center max-w-lg" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 size={54} className="text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-display text-5xl uppercase text-white">Confirming Payment</h1>
            <p className="mt-4 text-white/60">Hang tight while we verify your subscription…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={64} className="text-gold mx-auto mb-6" />
            <h1 className="font-display text-6xl uppercase text-white">You're In</h1>
            <p className="mt-4 text-white/65">Welcome to Fan Focus. Your subscription is active — our team will reach out with next steps shortly.</p>
            <Link to="/" data-testid="success-home" className="mt-8 inline-flex bg-gold text-black px-10 py-4 overline hover:bg-[#F5D061] transition-colors duration-300">Back Home</Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle size={64} className="text-white/50 mx-auto mb-6" />
            <h1 className="font-display text-5xl uppercase text-white">{state === "timeout" ? "Still Processing" : "Something Went Wrong"}</h1>
            <p className="mt-4 text-white/60">{state === "timeout" ? "Your payment is taking longer than expected. Check your email for confirmation." : "We couldn't confirm this payment. If you were charged, contact us and we'll sort it out."}</p>
            <Link to="/pricing" data-testid="success-retry" className="mt-8 inline-flex border border-gold text-gold px-10 py-4 overline hover:bg-gold hover:text-black transition-colors duration-300">Back to Pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
